// src/components/Common/SearchableSelect.js

import React, { useState, useMemo, useRef } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  InputAdornment,
  IconButton,
  Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

/**
 * SearchableSelect
 * Componente estándar de select con buscador integrado para APIM.
 * 
 * Modos:
 * 1. Múltiple (multiple={true}):
 *    - Buscador en tiempo real con lupa fija arriba.
 *    - Casilla de verificación (Checkbox) por cada opción.
 *    - Fila "Seleccionar todo" / "Seleccionar filtrados" con contador.
 * 
 * 2. Simple (multiple={false}):
 *    - Buscador en tiempo real con lupa fija arriba.
 *    - SIN checkboxes ni fila de seleccionar todo.
 *    - Selección directa con cierre inmediato del menú.
 */
const SearchableSelect = React.forwardRef(({ id,
  name,
  label,
  options = [],
  value,
  onChange,
  onValueChange,
  placeholder = "Seleccione...",
  multiple = false,
  searchable = true,
  disabled = false,
  fullWidth = true,
  size = "small",
  sx = {},
  selectSx = {},
  getOptionValue,
  getOptionLabel,
  renderValue: customRenderValue,
  error = false,
  required = false,
  inputRef: customInputRef,
}, ref) => {
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef(null);

  // Normalizar opciones a formato { value, label, raw }
  const normalizedOptions = useMemo(() => {
    if (!Array.isArray(options)) return [];
    return options.map((op) => {
      if (op === null || op === undefined) return { value: "", label: "", raw: op };
      if (typeof op === "string" || typeof op === "number" || typeof op === "boolean") {
        return { value: op, label: String(op), raw: op };
      }
      const val = getOptionValue
        ? getOptionValue(op)
        : op.value ?? op.id ?? op.eps ?? op.codigo ?? op.Consecutivo ?? "";
      const lbl = getOptionLabel
        ? getOptionLabel(op)
        : op.label ??
          op.descripcion ??
          op.eps ??
          op.nombre ??
          op.grupo ??
          op.subgrupo ??
          (op.Consecutivo ? `${op.Consecutivo} - ${op.Api}` : null) ??
          String(val);
      return { value: val, label: String(lbl), raw: op };
    });
  }, [options, getOptionValue, getOptionLabel]);

  // Manejo de valores seleccionados normalizados
  const selectedValues = useMemo(() => {
    if (multiple) {
      if (Array.isArray(value)) return value.map(String);
      if (value !== undefined && value !== null && value !== "") return [String(value)];
      return [];
    }
    return value !== undefined && value !== null ? String(value) : "";
  }, [value, multiple]);

  // Filtrado de opciones en tiempo real por término de búsqueda
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return normalizedOptions;
    const term = searchTerm.toLowerCase();
    return normalizedOptions.filter((op) =>
      op.label.toLowerCase().includes(term)
    );
  }, [normalizedOptions, searchTerm]);

  const totalOptions = normalizedOptions.length;
  const isAllSelected =
    multiple && totalOptions > 0 && selectedValues.length === totalOptions;
  const isSomeSelected =
    multiple && selectedValues.length > 0 && selectedValues.length < totalOptions;

  // Disparar cambio con evento sintético y valor directo
  const triggerChange = (newVal) => {
    const syntheticEvent = {
      target: {
        name: name || "",
        value: newVal,
      },
      stopPropagation: () => {},
      preventDefault: () => {},
    };

    if (onValueChange) {
      onValueChange(newVal);
    }
    if (onChange) {
      if (name) {
        onChange(syntheticEvent, newVal);
      } else {
        onChange(newVal, syntheticEvent);
      }
    }
  };

  // Manejador de "Seleccionar todo" en modo múltiple
  const handleSelectAll = () => {
    if (isAllSelected) {
      triggerChange([]);
    } else {
      if (searchTerm.trim() && filteredOptions.length < totalOptions) {
        const filteredValStrings = filteredOptions.map((o) => String(o.value));
        const combined = Array.from(
          new Set([...selectedValues, ...filteredValStrings])
        );
        triggerChange(combined);
      } else {
        triggerChange(normalizedOptions.map((o) => o.value));
      }
    }
  };

  // Manejador de selección de opción
  const handleSelectOption = (opVal) => {
    if (multiple) {
      const strVal = String(opVal);
      if (selectedValues.includes(strVal)) {
        triggerChange(selectedValues.filter((v) => v !== strVal));
      } else {
        triggerChange([...selectedValues, opVal]);
      }
    } else {
      triggerChange(opVal);
      setSearchTerm("");
    }
  };

  // Tooltip con los valores seleccionados
  const selectedTooltipText = useMemo(() => {
    if (multiple) {
      if (selectedValues.length === 0) return "";
      return selectedValues
        .map(
          (v) =>
            normalizedOptions.find((o) => String(o.value) === v)?.label || v
        )
        .join("\n");
    }
    const found = normalizedOptions.find((o) => String(o.value) === selectedValues);
    return found ? found.label : "";
  }, [selectedValues, normalizedOptions, multiple]);

  return (
    <FormControl
      size={size}
      disabled={disabled}
      fullWidth={fullWidth}
      error={error}
      required={required}
      sx={{
        minWidth: 140,
        ...sx,
      }}
    >
      {label && (
        <InputLabel
          sx={{
            fontSize: "var(--app-font-size)",
            backgroundColor: "white",
            px: 0.5,
          }}
        >
          {label}
        </InputLabel>
      )}

      <Select id={id}
        inputRef={ref || customInputRef}
        name={name}
        displayEmpty
        multiple={multiple}
        label={label}
        value={selectedValues}
        onClose={() => setSearchTerm("")}
        onOpen={() => {
          setTimeout(() => {
            searchInputRef.current?.focus();
          }, 50);
        }}
        renderValue={(selected) => {
          if (customRenderValue) {
            return customRenderValue(selected, normalizedOptions);
          }

          let text = "";
          if (multiple) {
            if (selectedValues.length === 0) {
              return <span style={{ color: "#94a3b8", fontFamily: "var(--font-body)" }}>{placeholder}</span>;
            } else if (isAllSelected) {
              text = `Todos (${totalOptions})`;
            } else if (selectedValues.length === 1) {
              const found = normalizedOptions.find(
                (o) => String(o.value) === selectedValues[0]
              );
              text = found ? found.label : selectedValues[0];
            } else {
              text = `${selectedValues.length} seleccionados`;
            }
          } else {
            if (selectedValues === "" || selectedValues === null || selectedValues === undefined) {
              return <span style={{ color: "#94a3b8", fontFamily: "var(--font-body)" }}>{placeholder}</span>;
            }
            const found = normalizedOptions.find(
              (o) => String(o.value) === selectedValues
            );
            text = found ? found.label : selectedValues;
          }

          return (
            <span
              title={selectedTooltipText}
              style={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "var(--app-font-size)",
                fontFamily: "var(--font-body)",
              }}
            >
              {text}
            </span>
          );
        }}
        MenuProps={{
          autoFocus: false,
          PaperProps: {
            sx: {
              maxHeight: 380,
              minWidth: 260,
              maxWidth: 500,
              overflowX: "hidden",
              boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
              borderRadius: "8px",
            },
          },
        }}
        sx={{
          fontSize: "var(--app-font-size)",
          fontFamily: "var(--font-body)",
          height: "calc(var(--app-font-size) * 2.5)",
          minHeight: "32px",
          backgroundColor: "white",
          borderRadius: "4px",
          "& .MuiSelect-select": {
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            py: "4px !important",
            pr: "32px !important",
            fontSize: "var(--app-font-size)",
            fontFamily: "var(--font-body)",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#cbd5e1",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#94a3b8",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#007bba",
          },
          ...selectSx,
        }}
      >
        {/* Cabecera pegajosa con el Buscador */}
        {searchable && (
          <Box
            sx={{
              p: 1,
              position: "sticky",
              top: 0,
              bgcolor: "background.paper",
              zIndex: 10,
              borderBottom: "1px solid #e2e8f0",
            }}
            onKeyDown={(e) => {
              if (e.key !== "Escape") e.stopPropagation();
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <TextField
              inputRef={searchInputRef}
              size="small"
              fullWidth
              placeholder={`Buscar ${label ? label.toLowerCase() : "opción"}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearchTerm("");
                        searchInputRef.current?.focus();
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: "var(--app-font-size)",
                  fontFamily: "var(--font-body)",
                  py: 0.5,
                },
              }}
            />
          </Box>
        )}

        {/* Opción "Seleccionar Todo" solo en selección múltiple */}
        {multiple && (
          <MenuItem
            dense
            onClick={(e) => {
              e.stopPropagation();
              handleSelectAll();
            }}
            sx={{
              fontWeight: 600,
              fontSize: "var(--app-font-size)",
              fontFamily: "var(--font-body)",
              borderBottom: "1px solid #e2e8f0",
              py: 0.5,
              bgcolor: isAllSelected ? "action.hover" : "inherit",
            }}
          >
            <Checkbox
              size="small"
              checked={isAllSelected}
              indeterminate={isSomeSelected && !isAllSelected}
              sx={{ p: 0.5 }}
            />
            <ListItemText
              primary={
                searchTerm.trim()
                  ? `Seleccionar filtrados (${filteredOptions.length})`
                  : `Seleccionar todo (${totalOptions})`
              }
              primaryTypographyProps={{
                fontSize: "var(--app-font-size)",
                fontFamily: "var(--font-body)",
                fontWeight: 600,
              }}
            />
          </MenuItem>
        )}

        {/* Opción para deseleccionar / vaciar en modo simple */}
        {!multiple && (
          <MenuItem
            dense
            value=""
            onClick={() => handleSelectOption("")}
            sx={{
              fontSize: "var(--app-font-size)",
              fontFamily: "var(--font-body)",
              fontStyle: "italic",
              color: "#94a3b8",
              py: 0.5,
            }}
          >
            {placeholder}
          </MenuItem>
        )}

        {/* Lista de Opciones Filtradas */}
        {filteredOptions.length === 0 ? (
          <MenuItem
            disabled
            sx={{
              fontSize: "var(--app-font-size)",
              fontFamily: "var(--font-body)",
              fontStyle: "italic",
              py: 1.5,
              justifyContent: "center",
            }}
          >
            No se encontraron resultados
          </MenuItem>
        ) : (
          filteredOptions.map((op) => {
            const isSelected = multiple
              ? selectedValues.includes(String(op.value))
              : String(op.value) === selectedValues;

            return (
              <MenuItem
                dense
                key={String(op.value)}
                value={op.value}
                onClick={() => handleSelectOption(op.value)}
                selected={isSelected}
                sx={{
                  fontSize: "var(--app-font-size)",
                  fontFamily: "var(--font-body)",
                  py: 0.5,
                  display: "flex",
                  alignItems: "center",
                  "&.Mui-selected": {
                    bgcolor: "rgba(0, 123, 186, 0.08)",
                    fontWeight: 600,
                  },
                }}
              >
                {/* Checkbox solo visible en selección múltiple */}
                {multiple && (
                  <Checkbox size="small" checked={isSelected} sx={{ p: 0.5 }} />
                )}
                <ListItemText
                  primary={op.label}
                  primaryTypographyProps={{
                    fontSize: "var(--app-font-size)",
                    fontFamily: "var(--font-body)",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    fontWeight: isSelected ? 600 : 400,
                  }}
                />
              </MenuItem>
            );
          })
        )}
      </Select>
    </FormControl>
  );
});

SearchableSelect.displayName = "SearchableSelect";

export default SearchableSelect;
